import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';

import { FieldEditorV2Component } from '../../base-template-editor-v2/field-editor-v2/field-editor-v2.component';
import { InlineEditV2Directive } from '../../base-template-editor-v2/inline-edit/inline-edit-v2.directive';
import { BaseComponent } from '../../common/BaseComponent';
import { Helper } from '../../../../utils/helper';
import {
  OnlyCtaInitModel,
  OnlyCtaInitVariables,
  OnlyCtaModel,
  OnlyCtaVariables,
} from '../../../../models/email-template/only-cta.interface';

/**
 * Mockup-styled Only CTA block - a drop-in replacement for app-only-cta in
 * block-renderer-v2 (same [block]/[isPreview] inputs and outputs), so it
 * covers both the center-canvas summary (isPreview=true) and the
 * right-panel edit form (isPreview=false). Reuses the real, unmodified
 * app-field-editor-v2 (incl. its segment dropdown/validation logic) and
 * app-pin sub-components and the exact same OnlyCtaVariables field names -
 * only the surrounding layout is new. Comments are reachable via the
 * separate Comments tab (see edit-block-comments-v2), same as the other
 * always-visible v2 blocks.
 */
@Component({
  selector: 'app-only-cta-v2',
  imports: [CommonModule, ReactiveFormsModule, FieldEditorV2Component, InlineEditV2Directive],
  templateUrl: './only-cta-v2.component.html',
  styleUrl: './only-cta-v2.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class OnlyCtaComponentV2 extends BaseComponent implements OnInit {
  @Input() block: OnlyCtaModel = new OnlyCtaInitModel();
  @Input() isPreview = false;
  @Input() disabled = false;

  @Output() blockData = new EventEmitter<any>();
  @Output() removeBlockEvent = new EventEmitter<string | null>();

  ngOnInit() {
    if (Helper.isEmpty(this.block.variables!)) {
      this.block.variables = new OnlyCtaInitVariables();
    }
  }

  onlyCtaTextPlaceholder = 'Enter CTA text';
  onlyCtaLinkPlaceholder = 'Enter CTA link';

  /** Center-canvas card's own placeholder for the CTA button - shown as the
   *  button label itself, same treatment as Intro + CTA's CTA. */
  protected readonly ctaButtonPlaceholder = 'Insert CTA text';

  /** The link opened by clicking the preview CTA - same treatment as Main
   *  Banner/Intro + CTA's previewHref: v1's exact helper.getFullUrl(OnlyCtaLink),
   *  plus a segment query param when a segment is selected
   *  (OnlyCtaLinkSegmentRegex is otherwise unused metadata today, v1 included). */
  get previewHref(): string {
    const base = this.helper.getFullUrl(this.block.variables?.['OnlyCtaLink']);
    const segment = this.block.variables?.['OnlyCtaLinkSegmentRegex'];
    if (!segment) {
      return base;
    }
    const separator = base.includes('?') ? '&' : '?';
    return `${base}${separator}segment=${encodeURIComponent(segment)}`;
  }

  removeBlock(): void {
    this.removeBlockEvent.emit(this.block.blockId);
  }

  onChange(value: any, fieldName: keyof OnlyCtaVariables) {
    if (this.block.variables) {
      (this.block.variables as any)[fieldName] = value;
      this.blockData.emit(this.block);
    }
  }


  handleOnSegmentChange(segment: string, blockName: string) {
    if (this.block.variables) {
      (this.block.variables as any)[blockName] = segment;
      this.blockData.emit(this.block);
    }
  }
}
